import React, { useState, useEffect } from 'react';
import { Search, BookOpen, MessageCircle, Heart, HeartOff, Minus, Database, Filter, Calendar } from 'lucide-react';
import './App.css';

function App() {
  const [word, setWord] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [contentfulWords, setContentfulWords] = useState([]);
  const [wordsLoading, setWordsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('newest');

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!word.trim()) return;

    setLoading(true);
    setError('');
    setResult(null);

    try {
      const response = await fetch(`https://tinydefine-backend.kixalize-aim.workers.dev/api/lookup?word=${encodeURIComponent(word.trim())}`);
      const data = await response.json();
      
      if (response.ok) {
        setResult(data);
      } else {
        setError(data.error || 'Failed to lookup word');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getSentimentIcon = (sentiment) => {
    switch (sentiment) {
      case 'positive':
        return <Heart className="sentiment-icon positive" />;
      case 'negative':
        return <HeartOff className="sentiment-icon negative" />;
      default:
        return <Minus className="sentiment-icon neutral" />;
    }
  };

  const getSentimentColor = (sentiment) => {
    switch (sentiment) {
      case 'positive':
        return '#10b981';
      case 'negative':
        return '#ef4444';
      default:
        return '#6b7280';
    }
  };

  // Check for word parameter in URL on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const wordParam = urlParams.get('word');
    if (wordParam) {
      setWord(wordParam);
      // Auto-search for the word
      setTimeout(() => {
        const form = document.querySelector('.search-form');
        if (form) form.dispatchEvent(new Event('submit', { bubbles: true }));
      }, 100);
    }
  }, []);

  // Fetch all words from Contentful on component mount
  useEffect(() => {
    const fetchWords = async () => {
      try {
        const response = await fetch('https://tinydefine-backend.kixalize-aim.workers.dev/api/words');
        if (response.ok) {
          const data = await response.json();
          setContentfulWords(data.words || []);
        } else {
          console.error('Failed to fetch words');
        }
      } catch (error) {
        console.error('Error fetching words:', error);
      } finally {
        setWordsLoading(false);
      }
    };

    fetchWords();
  }, []);

  // Filter and sort words
  const filteredAndSortedWords = contentfulWords
    .filter(word => 
      word.word.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      if (sortBy === 'newest') {
        return new Date(b.createdAt) - new Date(a.createdAt);
      } else if (sortBy === 'oldest') {
        return new Date(a.createdAt) - new Date(b.createdAt);
      } else if (sortBy === 'alphabetical') {
        return a.word.localeCompare(b.word);
      }
      return 0;
    });

  return (
    <div className="app">
      <div className="container">
        <header className="header">
          <h1 className="title">
            <BookOpen className="title-icon" />
            TinyDefine
          </h1>
          <p className="subtitle">Instant Word Meaning + Context</p>
        </header>

        <form onSubmit={handleSearch} className="search-form">
          <div className="search-container">
            <Search className="search-icon" />
            <input
              type="text"
              value={word}
              onChange={(e) => setWord(e.target.value)}
              placeholder="Type a word to define..."
              className="search-input"
              disabled={loading}
            />
            <button 
              type="submit" 
              className="search-button"
              disabled={loading || !word.trim()}
            >
              {loading ? 'Searching...' : 'Search'}
            </button>
          </div>
        </form>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {result && (
          <div className={`result-card ${result.found ? 'found' : 'not-found'}`}>
            <div className="result-header">
              <h2 className="result-word">{result.word}</h2>
              {result.found && (
                <div className="sentiment-badge" style={{ backgroundColor: getSentimentColor(result.sentiment) }}>
                  {getSentimentIcon(result.sentiment)}
                  <span>{result.sentiment}</span>
                </div>
              )}
            </div>
            
            <div className="result-content">
              <div className="definition-section">
                <h3 className="section-title">
                  <BookOpen className="section-icon" />
                  Definition
                </h3>
                <p className="definition-text">{result.definition}</p>
              </div>

              {result.found && (
                <div className="example-section">
                  <h3 className="section-title">
                    <MessageCircle className="section-icon" />
                    Example
                  </h3>
                  <p className="example-text">{result.example}</p>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="suggestions">
          <p className="suggestions-title">Try these words:</p>
          <div className="suggestion-chips">
            {['hello', 'love', 'beautiful', 'computer', 'music', 'family'].map((suggestion) => (
              <button
                key={suggestion}
                onClick={() => {
                  setWord(suggestion);
                  // Auto-search after a brief delay
                  setTimeout(() => {
                    const form = document.querySelector('.search-form');
                    if (form) form.dispatchEvent(new Event('submit', { bubbles: true }));
                  }, 100);
                }}
                className="suggestion-chip"
              >
                {suggestion}
              </button>
            ))}
          </div>
        </div>

        {/* Contentful Words Section */}
        <div className="contentful-section">
          <div className="section-header">
            <Database className="section-icon" />
            <h3 className="section-title">Published Words from CMS</h3>
          </div>
          
          {wordsLoading ? (
            <div className="loading-words">
              <div className="loading-spinner"></div>
              <p>Loading words from Contentful...</p>
            </div>
          ) : contentfulWords.length > 0 ? (
            <>
              {/* Search and Filter Controls */}
              <div className="cms-controls">
                <div className="search-filter-container">
                  <div className="search-box">
                    <Search className="search-icon" />
                    <input
                      type="text"
                      placeholder="Search words..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="cms-search-input"
                    />
                  </div>
                  <div className="filter-box">
                    <Calendar className="filter-icon" />
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="cms-filter-select"
                    >
                      <option value="newest">Newest First</option>
                      <option value="oldest">Oldest First</option>
                      <option value="alphabetical">A-Z</option>
                    </select>
                  </div>
                </div>
                <div className="results-count">
                  {filteredAndSortedWords.length} of {contentfulWords.length} words
                </div>
              </div>

              {/* Words Grid */}
              {filteredAndSortedWords.length > 0 ? (
                <div className="words-grid">
                  {filteredAndSortedWords.map((wordItem) => (
                    <div key={wordItem.id} className="word-card">
                      <div className="word-card-header">
                        <h4 className="word-card-title">{wordItem.word}</h4>
                        <span className="word-card-badge">CMS</span>
                      </div>
                      <div className="word-card-content">
                        <p className="word-card-date">
                          Added: {new Date(wordItem.createdAt).toLocaleDateString()}
                        </p>
                        <button
                          onClick={() => {
                            window.location.href = `?word=${encodeURIComponent(wordItem.word)}`;
                          }}
                          className="word-card-button"
                        >
                          View Details
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-results">
                  <p>No words match your search.</p>
                  <p className="no-results-hint">Try adjusting your search term or filter.</p>
                </div>
              )}
            </>
          ) : (
            <div className="no-words">
              <p>No words published in Contentful yet.</p>
              <p className="no-words-hint">Add words through your Contentful dashboard to see them here!</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default App; 